import { COUNTRIES, US_STATES } from '../../../common/constants'
import useToast from '../../../common/hooks/useToast'
import { moveArrayItem } from '../../../common/helpers'
import Break from '../Break'
import Button from '../Button'
import InlineStack from '../InlineStack'
import AddItemsInput from './AddItemsInput'
import SwitchInput from './SwitchInput'

const fieldTypes = [
    {
        value: 'text',
        label: 'Text',
    },
    {
        value: 'email',
        label: 'Email',
    },
    {
        value: 'tel',
        label: 'Tel',
    },
    {
        value: 'number',
        label: 'Number',
    },
    {
        value: 'textarea',
        label: 'Textarea',
    },
    {
        value: 'select',
        label: 'Select',
    },
    {
        value: 'date',
        label: 'Date',
    },
    {
        value: 'checkbox',
        label: 'Checkbox',
    },
]

const placeholderFieldTypes = ['text', 'email', 'tel', 'number', 'textarea']

const groupTypeOptions = [
    { value: 'repeatable', label: 'Repeatable' },
    { value: 'step', label: 'Step' },
]

export default function AddItemsFieldSet({
    title,
    data,
    setData,
    deleteFieldSet,
    moveFieldSetUp,
    moveFieldSetDown,
    errors,
    history,
    onMove,
    isStep,
    onGroupTypeChange,
}) {
    const setToast = useToast()

    const value = !title
        ? data.fields.filter((f) => !f.groupName)
        : data.fields.filter((f) => f.groupName === title)

    const indices = data.fields
        .map((f, i) => ({ f, i }))
        .filter(({ f }) => (title ? f.groupName === title : !f.groupName))
        .map(({ i }) => i)
    const firstIndex = indices[0]
    const lastIndex = indices[indices.length - 1]

    function handleChange(v) {
        const newValueForSet = title
            ? v.map((f) => ({
                  ...f,
                  groupName: title,
                  groupType: isStep ? 'step' : 'repeatable',
              }))
            : v
        const newValueForOtherSets = data.fields.filter((f) =>
            title ? f.groupName !== title : f.groupName,
        )

        const insertAt = firstIndex ?? newValueForOtherSets.length
        setData({
            ...data,
            fields: [
                ...newValueForOtherSets.slice(0, insertAt),
                ...newValueForSet,
                ...newValueForOtherSets.slice(insertAt),
            ],
        })
    }

    function handleItemDelete(item) {
        if (!history) {
            return
        }
        setToast(`Deleted "${item.label || 'field'}"`, 'success', {
            label: 'Undo',
            onClick: history.undo,
        })
    }

    function handleItemMove(i, item) {
        if (onMove) {
            onMove(indices[i], item)
        }
    }

    function handleMove(from, to) {
        setData({
            ...data,
            fields: moveArrayItem(data.fields, indices[from], indices[to]),
        })
    }
    return (
        <>
            {!title && history ? (
                <InlineStack spaceBetween itemsEnd>
                    <label>Fields</label>
                    <InlineStack gap={'sm'}>
                        <Button
                            text="Undo"
                            outline
                            tiny
                            disabled={!history.canUndo}
                            onClick={history.undo}
                        />
                        <Button
                            text="Redo"
                            outline
                            tiny
                            disabled={!history.canRedo}
                            onClick={history.redo}
                        />
                    </InlineStack>
                </InlineStack>
            ) : (
                <label>
                    {title
                        ? `${isStep ? 'Step' : 'Field group'}: ${title}`
                        : 'Fields'}
                </label>
            )}

            <Break />
            <Break />
            <Break />
            <AddItemsInput
                header={
                    title ? (
                        <>
                            {onGroupTypeChange ? (
                                <SwitchInput
                                    id={`group-type-${title}`}
                                    value={isStep ? 'step' : 'repeatable'}
                                    options={groupTypeOptions}
                                    onChange={(v) =>
                                        onGroupTypeChange(v === 'step')
                                    }
                                />
                            ) : null}
                            <small className="input-info">
                                {isStep
                                    ? 'Shown on its own page, with Next and Back buttons. Repeatable groups below it are shown on the same page.'
                                    : 'Users will be able to add multiple entries of this field group.'}
                            </small>
                            <InlineStack gap={'sm'}>
                                <Button
                                    destructive
                                    text="Delete group"
                                    outline
                                    tiny
                                    onClick={deleteFieldSet}
                                />
                                {moveFieldSetUp && (
                                    <Button
                                        text="Move up"
                                        outline
                                        tiny
                                        onClick={() =>
                                            moveFieldSetUp(firstIndex)
                                        }
                                    />
                                )}
                                {moveFieldSetDown && (
                                    <Button
                                        text="Move down"
                                        outline
                                        tiny
                                        onClick={() =>
                                            moveFieldSetDown(lastIndex)
                                        }
                                    />
                                )}
                            </InlineStack>
                            <br />
                        </>
                    ) : null
                }
                hasItemMenu
                onItemMove={onMove ? handleItemMove : undefined}
                onItemDelete={handleItemDelete}
                errors={errors}
                onChange={handleChange}
                value={value}
                moveMode
                onMove={handleMove}
                newItemTitle="New field"
                presets={[
                    {
                        label: 'First name',
                        fieldType: 'text',
                        required: true,
                    },
                    {
                        label: 'Last name',
                        fieldType: 'text',
                        required: true,
                    },
                    {
                        label: 'Email',
                        fieldType: 'email',
                        required: true,
                    },
                    {
                        label: 'Phone',
                        fieldType: 'tel',
                    },
                    {
                        label: 'Address',
                        fieldType: 'text',
                    },
                    {
                        label: 'City',
                        fieldType: 'text',
                    },
                    {
                        label: 'Province',
                        fieldType: 'text',
                    },
                    {
                        label: 'State',
                        fieldType: 'select',
                        options: [
                            '',
                            ...US_STATES.map(({ text }) => text),
                        ].join(','),
                    },
                    {
                        label: 'Country',
                        fieldType: 'select',
                        options: COUNTRIES.map(({ text }) => text).join(','),
                    },
                    {
                        label: 'ZIP',
                        fieldType: 'text',
                    },
                ]}
                innerInputs={[
                    {
                        key: 'label',
                        type: 'text',
                        label: 'Label',
                        required: true,
                        collapseInput: true,
                        getTags: (item) =>
                            String(item.required) === 'true'
                                ? ['Required']
                                : [],
                    },
                    {
                        key: 'fieldType',
                        type: 'select',
                        label: 'Field type',
                        options: fieldTypes,
                    },
                    {
                        key: 'required',
                        type: 'select',
                        label: 'Required',
                        options: [
                            {
                                label: 'No',
                                value: false,
                            },
                            {
                                label: 'Yes',
                                value: true,
                            },
                        ],
                    },
                    {
                        key: 'options',
                        type: 'text',
                        label: 'Options (comma separated)',
                        getHidden: (item) => item.fieldType !== 'select',
                    },
                    {
                        key: 'placeholder',
                        type: 'text',
                        label: 'Placeholder',
                        getHidden: (item) =>
                            !placeholderFieldTypes.includes(
                                item.fieldType || 'text',
                            ),
                    },
                    {
                        key: 'isAppUserField',
                        type: 'select',
                        label: 'Use as app user field',
                        infoBottom:
                            'Use field as a user field and register a user in the aCo app. This will also notify the user via email. Compatible fields: Email, First name, Last name, Address, City, State, Zip, Country, Phone, Company (when present on the main field group, will be used for all field group users). Any other fields will be stored as user metafields.',
                        options: [
                            {
                                label: 'No',
                                value: false,
                            },
                            {
                                label: 'Yes',
                                value: true,
                            },
                        ],
                    },
                    {
                        key: 'groupName',
                        type: 'hidden',
                        value: title,
                    },
                ]}
            />
        </>
    )
}
